import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Clock,
    ShieldCheck,
    ChevronRight,
    ChevronLeft,
    AlertCircle,
    Loader2,
    CheckCircle2,
    Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SECTIONS, ALL_QUESTION_IDS } from "@/data/questions";

const APPS_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbyCjuux0wjayABlFVEqVBp7XWRKRelWGs3Ncp6NQhmlMimt0iSIOnBOsN69t5S6I17lYA/exec";
const TCC_LOGO_MARK = "/tcc_logo_mark.png";

const GRADE_OPTIONS = [
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
];

const emptyAbout = {
    firstname: "",
    lastname: "",
    email: "",
    age: "",
    grade: "",
    school: "",
    city: "",
    country: "India",
    extras: "",
    achievements: "",
    query: "",
};

export default function Assessment() {
    const [params] = useSearchParams();
    // Accept both `pid` (used by the checkout-dialog flow) and
    // `razorpay_payment_id` (used by Razorpay Payment Link redirects).
    const pid = (params.get("pid") || params.get("razorpay_payment_id") || "").trim();
    // Coupon + amount forwarded from the Razorpay verify redirect so we can
    // pass them straight through to the Google Sheet.
    const couponCodeFromUrl = (params.get("code") || "").trim();
    const amountPaiseFromUrl = (params.get("amount") || "").trim();

    // Payment gate — if no pid, show blocked screen immediately.
    const [step, setStep] = useState(pid ? "welcome" : "blocked");
    const [about, setAbout] = useState(() => {
        const nameParam = (params.get("name") || "").trim();
        const emailParam = (params.get("email") || "").trim();
        const [firstname, ...rest] = nameParam.split(" ");
        return {
            ...emptyAbout,
            firstname: firstname || "",
            lastname: rest.join(" ") || "",
            email: emailParam || "",
        };
    });
    const [answers, setAnswers] = useState({});
    const [aboutErrors, setAboutErrors] = useState({});
    const [testMissing, setTestMissing] = useState([]);
    const [submitError, setSubmitError] = useState(null);
    // Scroll to top on step change.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [step]);

    // ---- Validation helpers ---------------------------------------------
    const validateAbout = () => {
        const e = {};
        if (!about.firstname.trim()) e.firstname = "First name is required.";
        if (!about.lastname.trim()) e.lastname = "Last name is required.";
        if (!/^\S+@\S+\.\S+$/.test(about.email.trim())) e.email = "Enter a valid email.";
        if (!about.age.toString().trim()) e.age = "Age is required.";
        else if (!/^\d+$/.test(about.age.toString().trim())) e.age = "Age must be a number.";
        if (!about.grade) e.grade = "Please select a grade.";
        if (!about.school.trim()) e.school = "School is required.";
        if (!about.city.trim()) e.city = "City is required.";
        if (!about.country.trim()) e.country = "Country is required.";
        if (!about.extras.trim()) e.extras = "Please share at least one activity.";
        return e;
    };

    const answeredCount = useMemo(
        () => ALL_QUESTION_IDS.filter((id) => answers[id]).length,
        [answers]
    );
    const totalQuestions = ALL_QUESTION_IDS.length;

    // ---- Handlers -------------------------------------------------------
    const handleAboutNext = () => {
        const e = validateAbout();
        setAboutErrors(e);
        if (Object.keys(e).length > 0) {
            // Scroll to the first error
            const firstKey = Object.keys(e)[0];
            setTimeout(() => {
                const el = document.querySelector(`[data-error-key="${firstKey}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    const input = el.querySelector("input, select, textarea");
                    if (input) input.focus({ preventScroll: true });
                }
            }, 0);
            return;
        }
        setStep("instructions");
    };

    const handleAnswer = (qid, value) => {
        setAnswers((prev) => ({ ...prev, [qid]: value }));
        // Clear this from the missing list if it was flagged.
        setTestMissing((prev) => prev.filter((x) => x !== qid));
    };

    const handleSubmitTest = () => {
        const missing = ALL_QUESTION_IDS.filter((id) => !answers[id]);
        setTestMissing(missing);
        if (missing.length > 0) {
            setTimeout(() => {
                const el = document.querySelector(`[data-question-id="${missing[0]}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 0);
            return;
        }
        submitAssessment();
    };

    const submitAssessment = async () => {
        setStep("submitting");
        setSubmitError(null);
        const payload = {
            paymentId: pid,
            firstname: about.firstname.trim(),
            lastname: about.lastname.trim(),
            name: `${about.firstname.trim()} ${about.lastname.trim()}`.trim(),
            email: about.email.trim(),
            age: about.age.toString().trim(),
            grade: about.grade,
            school: about.school.trim(),
            city: about.city.trim(),
            country: about.country.trim(),
            extras: about.extras.trim(),
            achievements: about.achievements.trim(),
            query: about.query.trim(),
            // Forwarded from the Razorpay verify redirect (server-set).
            // NOTE: the current Apps Script `submitAssessment` only reads the
            // 13 keys documented in the spec — it will silently drop these
            // two until the Apps Script is updated to persist them into the
            // Google Sheet row.
            couponCode: couponCodeFromUrl,
            amountCharged: amountPaiseFromUrl,
            ...answers,
        };

        try {
            const res = await fetch(APPS_SCRIPT_URL, {
                method: "POST",
                // text/plain avoids CORS preflight — Apps Script parses JSON regardless.
                headers: { "Content-Type": "text/plain;charset=utf-8" },
                body: JSON.stringify(payload),
                redirect: "follow",
            });
            const data = await res.json();
            if (data && data.success === true) {
                // Tell our own backend so the admin's rescue list stops
                // flagging this parent. Best-effort — a failure here does not
                // affect the parent's Thank You screen.
                try {
                    await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/leads/mark-assessment-submitted`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ pid }),
                        }
                    );
                } catch { /* ignore */ }
                setStep("thankyou");
                return;
            }
            const err = (data && data.error) || "unknown";
            if (err === "duplicate") {
                setStep("duplicate");
                return;
            }
            if (err === "no_payment") {
                setStep("blocked");
                return;
            }
            // no_data or a backend exception — retry-able.
            setSubmitError(err);
            setStep("error");
        } catch (e) {
            // network / parse failure — retry-able. Answers preserved.
            setSubmitError(e?.message || "network");
            setStep("error");
        }
    };

    // ---- Render ---------------------------------------------------------
    return (
        <div className="min-h-screen bg-aq-cream text-aq-charcoal">
            <TopBar step={step} pid={pid} />
            <AnimatePresence mode="wait">
                {step === "blocked" && <BlockedScreen key="blocked" pid={pid} />}
                {step === "welcome" && (
                    <WelcomeScreen key="welcome" onStart={() => setStep("about")} />
                )}
                {step === "about" && (
                    <AboutScreen
                        key="about"
                        about={about}
                        setAbout={setAbout}
                        errors={aboutErrors}
                        onBack={() => setStep("welcome")}
                        onNext={handleAboutNext}
                    />
                )}
                {step === "instructions" && (
                    <InstructionsScreen
                        key="instructions"
                        onBack={() => setStep("about")}
                        onStart={() => setStep("test")}
                    />
                )}
                {step === "test" && (
                    <TestScreen
                        key="test"
                        answers={answers}
                        onAnswer={handleAnswer}
                        missing={testMissing}
                        answeredCount={answeredCount}
                        total={totalQuestions}
                        onSubmit={handleSubmitTest}
                    />
                )}
                {step === "submitting" && <SubmittingScreen key="submitting" />}
                {step === "thankyou" && (
                    <ThankYouScreen key="thankyou" name={about.firstname} email={about.email} />
                )}
                {step === "duplicate" && <DuplicateScreen key="duplicate" />}
                {step === "error" && (
                    <ErrorScreen
                        key="error"
                        error={submitError}
                        onRetry={submitAssessment}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

// -----------------------------------------------------------------------
// Top bar
// -----------------------------------------------------------------------
function TopBar({ step, pid }) {
    return (
        <div className="border-b border-aq-border bg-aq-cream/90 backdrop-blur sticky top-0 z-40">
            <div className="max-w-4xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between gap-4">
                <Link
                    to="/"
                    className="flex items-center gap-2.5"
                    data-testid="assessment-brand-link"
                >
                    <img
                        src={TCC_LOGO_MARK}
                        alt="TheCounselCo"
                        className="w-9 h-9 object-contain"
                    />
                    <div className="leading-tight">
                        <div className="font-serif text-lg text-aq-navy tracking-tight">
                            Academi<span className="text-aq-gold">Q</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-aq-muted">
                            The Assessment
                        </div>
                    </div>
                </Link>
                {pid && step !== "blocked" && (
                    <div
                        className="hidden sm:flex items-center gap-1.5 text-xs text-aq-muted"
                        data-testid="assessment-pid-badge"
                    >
                        <ShieldCheck className="w-3.5 h-3.5 text-aq-gold" />
                        <span>Payment verified</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// Screen shell wrapper
// -----------------------------------------------------------------------
const screenAnim = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
};

function ScreenShell({ children, testId, wide = false }) {
    return (
        <motion.section
            {...screenAnim}
            className={`mx-auto px-5 md:px-8 py-10 md:py-16 ${wide ? "max-w-4xl" : "max-w-2xl"}`}
            data-testid={testId}
        >
            {children}
        </motion.section>
    );
}

// -----------------------------------------------------------------------
// 1. Welcome
// -----------------------------------------------------------------------
function WelcomeScreen({ onStart }) {
    return (
        <ScreenShell testId="screen-welcome">
            <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                Welcome
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-aq-navy leading-[1.1] mb-6">
                You've paid — let's find out what your child is actually good at.
            </h1>
            <p className="text-aq-muted text-lg leading-relaxed mb-8">
                This is the AcademiQ Assessment — a counsellor-built profile that maps 8
                Multiple Intelligences and 6 Real-World Quotients. Your child's answers
                will be personally reviewed by Hemangi Shah before your report is sent.
            </p>

            <div className="space-y-3 mb-10">
                {[
                    { icon: <Clock className="w-4 h-4" />, t: "Takes about 30 minutes — best done in one sitting." },
                    { icon: <Sparkles className="w-4 h-4" />, t: "No right or wrong answers — honesty gives the truest read." },
                    { icon: <ShieldCheck className="w-4 h-4" />, t: "Your responses are private and used only to prepare the report." },
                ].map((row) => (
                    <div
                        key={row.t}
                        className="flex items-start gap-3 rounded-2xl border border-aq-border bg-white px-4 py-3.5"
                    >
                        <span className="mt-0.5 w-7 h-7 rounded-full bg-aq-navy text-aq-gold flex items-center justify-center shrink-0">
                            {row.icon}
                        </span>
                        <span className="text-aq-charcoal">{row.t}</span>
                    </div>
                ))}
            </div>

            <Button
                onClick={onStart}
                data-testid="welcome-start-btn"
                className="w-full sm:w-auto h-14 px-8 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base shadow-[0_10px_30px_-12px_rgba(200,150,44,0.6)] hover:-translate-y-0.5 transition-all"
            >
                Let's begin <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
        </ScreenShell>
    );
}

// -----------------------------------------------------------------------
// 2. About You
// -----------------------------------------------------------------------
function AboutScreen({ about, setAbout, errors, onBack, onNext }) {
    const set = (k) => (e) =>
        setAbout((prev) => ({ ...prev, [k]: e.target.value }));

    return (
        <ScreenShell testId="screen-about">
            <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                Step 1 of 3 — About the student
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl text-aq-navy leading-[1.15] mb-3">
                Tell us a little about your child.
            </h1>
            <p className="text-aq-muted mb-8">
                So the counsellor's notes on the report can be genuinely personal.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
                <Field
                    label="First name"
                    value={about.firstname}
                    onChange={set("firstname")}
                    error={errors.firstname}
                    errorKey="firstname"
                    testId="about-firstname"
                    autoComplete="given-name"
                />
                <Field
                    label="Last name"
                    value={about.lastname}
                    onChange={set("lastname")}
                    error={errors.lastname}
                    errorKey="lastname"
                    testId="about-lastname"
                    autoComplete="family-name"
                />
                <Field
                    label="Student's email"
                    type="email"
                    value={about.email}
                    onChange={set("email")}
                    error={errors.email}
                    errorKey="email"
                    testId="about-email"
                    autoComplete="email"
                />
                <Field
                    label="Age"
                    type="text"
                    inputMode="numeric"
                    value={about.age}
                    onChange={set("age")}
                    error={errors.age}
                    errorKey="age"
                    testId="about-age"
                />
                <SelectField
                    label="Grade"
                    value={about.grade}
                    onChange={set("grade")}
                    error={errors.grade}
                    errorKey="grade"
                    testId="about-grade"
                    placeholder="Select grade"
                    options={GRADE_OPTIONS}
                />
                <Field
                    label="School"
                    value={about.school}
                    onChange={set("school")}
                    error={errors.school}
                    errorKey="school"
                    testId="about-school"
                />
                <Field
                    label="City"
                    value={about.city}
                    onChange={set("city")}
                    error={errors.city}
                    errorKey="city"
                    testId="about-city"
                />
                <Field
                    label="Country"
                    value={about.country}
                    onChange={set("country")}
                    error={errors.country}
                    errorKey="country"
                    testId="about-country"
                />
            </div>

            <div className="mt-4 space-y-4">
                <TextareaField
                    label="Extracurricular activities"
                    value={about.extras}
                    onChange={set("extras")}
                    error={errors.extras}
                    errorKey="extras"
                    testId="about-extras"
                    placeholder="e.g. football, debate, coding club, painting…"
                />
                <TextareaField
                    label="Achievements (optional)"
                    value={about.achievements}
                    onChange={set("achievements")}
                    errorKey="achievements"
                    testId="about-achievements"
                    placeholder="Awards, roles, milestones. Leave blank if you'd rather not share."
                />
                <TextareaField
                    label="Any specific question for the counsellor? (optional)"
                    value={about.query}
                    onChange={set("query")}
                    errorKey="query"
                    testId="about-query"
                    placeholder="e.g. Should we consider Commerce? What extra activities help?"
                />
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-10">
                <Button
                    onClick={onBack}
                    variant="ghost"
                    data-testid="about-back-btn"
                    className="h-12 px-6 rounded-full text-aq-navy hover:bg-aq-navy/5"
                >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                    onClick={onNext}
                    data-testid="about-next-btn"
                    className="h-14 px-8 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold shadow-md hover:-translate-y-0.5 transition-all"
                >
                    Continue <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
            </div>
        </ScreenShell>
    );
}

function Field({ label, error, errorKey, testId, ...rest }) {
    return (
        <div data-error-key={errorKey}>
            <Label className="text-sm text-aq-navy font-semibold mb-1.5 block">
                {label}
            </Label>
            <Input
                {...rest}
                data-testid={testId}
                className={`h-12 rounded-xl border ${
                    error ? "border-red-500 focus-visible:ring-red-500/30" : "border-aq-border"
                } bg-white`}
            />
            {error && (
                <p
                    className="text-xs text-red-600 mt-1.5 flex items-center gap-1"
                    data-testid={`${testId}-error`}
                >
                    <AlertCircle className="w-3.5 h-3.5" /> {error}
                </p>
            )}
        </div>
    );
}

function SelectField({ label, value, onChange, error, errorKey, testId, placeholder, options }) {
    return (
        <div data-error-key={errorKey}>
            <Label className="text-sm text-aq-navy font-semibold mb-1.5 block">
                {label}
            </Label>
            <select
                value={value}
                onChange={onChange}
                data-testid={testId}
                className={`h-12 w-full rounded-xl border px-3 bg-white text-aq-charcoal ${
                    error ? "border-red-500" : "border-aq-border"
                }`}
            >
                <option value="">{placeholder}</option>
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
            {error && (
                <p
                    className="text-xs text-red-600 mt-1.5 flex items-center gap-1"
                    data-testid={`${testId}-error`}
                >
                    <AlertCircle className="w-3.5 h-3.5" /> {error}
                </p>
            )}
        </div>
    );
}

function TextareaField({ label, value, onChange, error, errorKey, testId, placeholder }) {
    return (
        <div data-error-key={errorKey}>
            <Label className="text-sm text-aq-navy font-semibold mb-1.5 block">
                {label}
            </Label>
            <textarea
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                rows={3}
                data-testid={testId}
                className={`w-full rounded-xl border px-3.5 py-2.5 bg-white text-aq-charcoal ${
                    error ? "border-red-500" : "border-aq-border"
                }`}
            />
            {error && (
                <p
                    className="text-xs text-red-600 mt-1.5 flex items-center gap-1"
                    data-testid={`${testId}-error`}
                >
                    <AlertCircle className="w-3.5 h-3.5" /> {error}
                </p>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------
// 3. Instructions
// -----------------------------------------------------------------------
function InstructionsScreen({ onBack, onStart }) {
    return (
        <ScreenShell testId="screen-instructions">
            <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                Step 2 of 3 — Before you start
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl text-aq-navy leading-[1.15] mb-3">
                How the test works.
            </h1>
            <p className="text-aq-muted mb-8">
                94 questions across 3 short sections. About 30 minutes. Answer honestly —
                your instinct is more useful than deliberation.
            </p>

            <div className="space-y-4">
                <InstructionCard
                    n="01"
                    title="Section 1 — Multiple Intelligences"
                    body="A scale of 4. Choose how much each statement sounds like you: 1 = Not at all, 4 = Very much like me."
                />
                <InstructionCard
                    n="02"
                    title="Section 2 — Real World Quotient"
                    body="A scale of 5. 1 = Strongly disagree, 5 = Strongly agree. Rate how each statement fits your real life."
                />
                <InstructionCard
                    n="03"
                    title="Section 3 — Scenarios & Knowledge"
                    body="Multiple-choice. Pick the option that best describes how you'd actually respond, or the answer you think is correct."
                />
            </div>

            <div className="mt-8 rounded-2xl border border-aq-border bg-aq-cream-warm p-5">
                <p className="text-sm text-aq-charcoal leading-relaxed">
                    <ShieldCheck className="inline w-4 h-4 text-aq-gold mr-1.5 -mt-0.5" />
                    Your progress is held in this window. Don't refresh mid-test — finish
                    in one sitting for the most accurate profile.
                </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-10">
                <Button
                    onClick={onBack}
                    variant="ghost"
                    data-testid="instructions-back-btn"
                    className="h-12 px-6 rounded-full text-aq-navy hover:bg-aq-navy/5"
                >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                    onClick={onStart}
                    data-testid="instructions-start-btn"
                    className="h-14 px-8 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold shadow-md hover:-translate-y-0.5 transition-all"
                >
                    Start the test <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
            </div>
        </ScreenShell>
    );
}

function InstructionCard({ n, title, body }) {
    return (
        <div className="rounded-2xl border border-aq-border bg-white p-5 flex gap-4">
            <div className="font-serif text-aq-gold text-2xl leading-none shrink-0">
                {n}
            </div>
            <div>
                <div className="font-serif text-lg text-aq-navy leading-tight mb-1">
                    {title}
                </div>
                <div className="text-aq-muted text-sm leading-relaxed">{body}</div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// 4. The Test
// -----------------------------------------------------------------------
function TestScreen({ answers, onAnswer, missing, answeredCount, total, onSubmit }) {
    const pct = Math.round((answeredCount / total) * 100);
    return (
        <motion.section
            {...screenAnim}
            className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12"
            data-testid="screen-test"
        >
            {/* Sticky progress */}
            <div
                className="sticky top-[62px] z-30 -mx-5 md:mx-0 px-5 md:px-6 py-3 md:rounded-2xl bg-aq-cream/95 backdrop-blur border-b md:border border-aq-border md:shadow-sm mb-6 md:mb-8"
                data-testid="test-progress-bar"
            >
                <div className="flex items-center justify-between text-xs text-aq-muted mb-2">
                    <span className="font-semibold text-aq-navy">
                        {answeredCount} of {total} answered
                    </span>
                    <span>{pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-aq-border overflow-hidden">
                    <div
                        className="h-full bg-aq-gold transition-all duration-500"
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>

            {missing.length > 0 && (
                <div
                    className="mb-6 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2"
                    data-testid="test-missing-banner"
                >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                        {missing.length} question{missing.length === 1 ? "" : "s"} still need
                        an answer. We've highlighted them in red — the first one is right
                        below.
                    </span>
                </div>
            )}

            {SECTIONS.map((section) => (
                <div key={section.id} className="mb-10">
                    <div className="mb-6">
                        <p className="text-xs uppercase tracking-[0.18em] text-aq-gold font-semibold mb-1">
                            Section {section.badge}
                        </p>
                        <h2 className="font-serif text-2xl md:text-3xl text-aq-navy leading-tight">
                            {section.label}
                        </h2>
                    </div>

                    {section.groups.map((group) => (
                        <div key={group.title} className="mb-8">
                            <div className="mb-4 border-l-2 border-aq-gold pl-4">
                                <div className="font-serif text-lg text-aq-navy leading-snug">
                                    {group.title}
                                </div>
                                <div className="text-sm text-aq-muted">{group.sub}</div>
                            </div>
                            <div className="space-y-3">
                                {group.questions.map((q) => (
                                    <QuestionRow
                                        key={q.id}
                                        q={q}
                                        type={section.type}
                                        value={answers[q.id]}
                                        onSelect={(v) => onAnswer(q.id, v)}
                                        isMissing={missing.includes(q.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            <div className="mt-4 border-t border-aq-border pt-8">
                <Button
                    onClick={onSubmit}
                    data-testid="test-submit-btn"
                    className="w-full sm:w-auto h-14 px-10 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base shadow-[0_10px_30px_-12px_rgba(200,150,44,0.6)] hover:-translate-y-0.5 transition-all"
                >
                    Submit my answers <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <p className="text-xs text-aq-muted mt-3">
                    Once submitted, Hemangi will personally review your profile and email
                    the report to the address you provided.
                </p>
            </div>
        </motion.section>
    );
}

const LIKERT4_LABELS = ["Not at all", "A little", "Somewhat", "Very much"];
const LIKERT5_LABELS = [
    "Strongly disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly agree",
];

function QuestionRow({ q, type, value, onSelect, isMissing }) {
    const isMcq = type === "mcq";
    const scale = type === "likert4" ? 4 : type === "likert5" ? 5 : q.o?.length || 4;
    return (
        <div
            data-question-id={q.id}
            data-testid={`question-${q.id}`}
            className={`rounded-2xl border p-4 md:p-5 bg-white transition-colors ${
                isMissing ? "border-red-400 bg-red-50/60" : "border-aq-border"
            }`}
        >
            <div className="text-aq-charcoal text-[15px] md:text-base leading-snug mb-3">
                <span className="text-aq-muted text-xs mr-2 font-mono">{q.id}</span>
                {q.t}
            </div>

            {isMcq ? (
                <div className="space-y-2">
                    {q.o.map((opt, i) => {
                        const v = i + 1;
                        const active = value === v;
                        return (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => onSelect(v)}
                                data-testid={`option-${q.id}-${v}`}
                                className={`w-full text-left rounded-xl border px-4 py-3 flex items-start gap-3 transition-all ${
                                    active
                                        ? "border-aq-gold bg-aq-gold/10 shadow-sm"
                                        : "border-aq-border bg-white hover:border-aq-navy/40"
                                }`}
                            >
                                <span
                                    className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-semibold ${
                                        active
                                            ? "border-aq-gold bg-aq-gold text-aq-navy"
                                            : "border-aq-border text-aq-muted"
                                    }`}
                                >
                                    {String.fromCharCode(64 + v)}
                                </span>
                                <span
                                    className={`text-sm md:text-[15px] leading-snug ${
                                        active ? "text-aq-navy font-medium" : "text-aq-charcoal"
                                    }`}
                                >
                                    {opt}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <LikertRow
                    qid={q.id}
                    scale={scale}
                    value={value}
                    onSelect={onSelect}
                    labels={scale === 4 ? LIKERT4_LABELS : LIKERT5_LABELS}
                />
            )}
        </div>
    );
}

function LikertRow({ qid, scale, value, onSelect, labels }) {
    return (
        <div>
            <div className="flex gap-1.5 sm:gap-2">
                {Array.from({ length: scale }, (_, i) => i + 1).map((v) => {
                    const active = value === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => onSelect(v)}
                            data-testid={`option-${qid}-${v}`}
                            className={`flex-1 h-11 rounded-xl border font-semibold text-sm transition-all ${
                                active
                                    ? "border-aq-gold bg-aq-gold text-aq-navy shadow-sm scale-[1.02]"
                                    : "border-aq-border bg-white text-aq-muted hover:border-aq-navy/40 hover:text-aq-navy"
                            }`}
                            aria-label={`${labels[v - 1]} (${v})`}
                        >
                            {v}
                        </button>
                    );
                })}
            </div>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-aq-muted mt-1.5 px-1">
                <span>{labels[0]}</span>
                <span>{labels[labels.length - 1]}</span>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// 5. Submitting
// -----------------------------------------------------------------------
function SubmittingScreen() {
    return (
        <ScreenShell testId="screen-submitting">
            <div className="text-center py-12">
                <Loader2 className="w-12 h-12 text-aq-gold mx-auto animate-spin mb-6" />
                <h1 className="font-serif text-3xl text-aq-navy mb-3">
                    Sending your answers…
                </h1>
                <p className="text-aq-muted max-w-md mx-auto">
                    Please don't close this window. This usually takes a few seconds.
                </p>
            </div>
        </ScreenShell>
    );
}

// -----------------------------------------------------------------------
// 6. Thank You
// -----------------------------------------------------------------------
function ThankYouScreen({ name, email }) {
    return (
        <ScreenShell testId="screen-thankyou">
            <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-aq-gold/15 mx-auto mb-6 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-aq-gold" />
                </div>
                <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                    All done
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl text-aq-navy leading-[1.1] mb-5">
                    Thank you{name ? `, ${name}` : ""}.
                </h1>
                <p className="text-aq-muted text-lg leading-relaxed max-w-lg mx-auto mb-6">
                    Your answers are safely with us. Hemangi will personally review your
                    profile and email your AcademiQ report to{" "}
                    <span className="font-semibold text-aq-navy">{email || "you"}</span>{" "}
                    within 24–48 hours.
                </p>

                <div
                    className="max-w-lg mx-auto mb-8 rounded-2xl border-l-4 border-aq-gold bg-aq-cream-warm px-5 py-4 text-left"
                    data-testid="thankyou-spam-notice"
                >
                    <div className="text-sm text-aq-navy font-semibold mb-1">
                        Not seeing our email? Check your Spam or Promotions folder.
                    </div>
                    <div className="text-sm text-aq-muted leading-relaxed">
                        Please mark our email as <b>"Not Spam"</b> so your child's report reaches
                        your inbox within 24–48 hours.
                    </div>
                </div>

                <div className="rounded-2xl border border-aq-border bg-white p-5 max-w-md mx-auto text-left mb-8">
                    <div className="text-xs uppercase tracking-[0.18em] text-aq-gold font-semibold mb-2">
                        What happens next
                    </div>
                    <ol className="text-sm text-aq-charcoal space-y-2 list-decimal pl-4">
                        <li>Hemangi personally reviews your child's profile.</li>
                        <li>You receive the full PDF report on WhatsApp & email.</li>
                        <li>Questions afterwards? Just reply — we'll help.</li>
                    </ol>
                </div>

                <Link
                    to="/"
                    data-testid="thankyou-home-btn"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-aq-navy text-aq-cream font-semibold hover:bg-aq-navy-deep transition-all"
                >
                    Back to home
                </Link>
            </div>
        </ScreenShell>
    );
}

// -----------------------------------------------------------------------
// Blocked (no valid pid)
// -----------------------------------------------------------------------
function BlockedScreen({ pid }) {
    return (
        <ScreenShell testId="screen-blocked">
            <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-aq-navy mx-auto mb-5" />
                <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                    Payment required
                </p>
                <h1 className="font-serif text-3xl sm:text-4xl text-aq-navy leading-[1.15] mb-4">
                    {pid
                        ? "This link is no longer valid."
                        : "The assessment is open to families who've paid."}
                </h1>
                <p className="text-aq-muted max-w-md mx-auto mb-8">
                    {pid
                        ? "It looks like this payment link has already been used or the assessment has been submitted. If this seems wrong, email us."
                        : "To take the AcademiQ assessment, please head back and complete the ₹2,999 payment. You'll be brought straight here afterwards."}
                </p>
                <Link
                    to="/"
                    data-testid="blocked-home-btn"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold shadow-md transition-all"
                >
                    Go to the payment page <ChevronRight className="w-4 h-4" />
                </Link>
                <p className="text-xs text-aq-muted mt-4">
                    Or email <a className="underline" href="mailto:thecounselco@gmail.com">thecounselco@gmail.com</a>
                </p>
            </div>
        </ScreenShell>
    );
}

// -----------------------------------------------------------------------
// Duplicate
// -----------------------------------------------------------------------
function DuplicateScreen() {
    return (
        <ScreenShell testId="screen-duplicate">
            <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-aq-navy mx-auto mb-5" />
                <p className="text-aq-gold uppercase tracking-[0.2em] text-xs mb-3">
                    Already submitted
                </p>
                <h1 className="font-serif text-3xl sm:text-4xl text-aq-navy leading-[1.15] mb-4">
                    This assessment has already been submitted.
                </h1>
                <p className="text-aq-muted max-w-md mx-auto mb-8">
                    Each payment link can be used once. Hemangi is already preparing the
                    report — you'll receive it on WhatsApp & email within 24–48 hours.
                </p>
                <Link
                    to="/"
                    data-testid="duplicate-home-btn"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-aq-navy text-aq-cream font-semibold hover:bg-aq-navy-deep transition-all"
                >
                    Back to home
                </Link>
                <p className="text-xs text-aq-muted mt-4">
                    Need help? Email{" "}
                    <a className="underline" href="mailto:thecounselco@gmail.com">
                        thecounselco@gmail.com
                    </a>
                </p>
            </div>
        </ScreenShell>
    );
}

// -----------------------------------------------------------------------
// Error (retry-able)
// -----------------------------------------------------------------------
function ErrorScreen({ error, onRetry }) {
    return (
        <ScreenShell testId="screen-error">
            <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-5" />
                <p className="text-red-600 uppercase tracking-[0.2em] text-xs mb-3">
                    Something went wrong
                </p>
                <h1 className="font-serif text-3xl sm:text-4xl text-aq-navy leading-[1.15] mb-4">
                    We couldn't send your answers.
                </h1>
                <p className="text-aq-muted max-w-md mx-auto mb-2">
                    Don't worry — your answers are safe. Just tap retry.
                </p>
                {error && (
                    <p
                        className="text-xs text-aq-muted/70 mb-8 font-mono"
                        data-testid="error-detail"
                    >
                        ({error})
                    </p>
                )}
                <Button
                    onClick={onRetry}
                    data-testid="error-retry-btn"
                    className="h-12 px-8 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold shadow-md transition-all"
                >
                    Try again
                </Button>
                <p className="text-xs text-aq-muted mt-4">
                    Still stuck? Email{" "}
                    <a className="underline" href="mailto:thecounselco@gmail.com">
                        thecounselco@gmail.com
                    </a>{" "}
                    — we'll sort it out.
                </p>
            </div>
        </ScreenShell>
    );
}
